import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon } from '@heroicons/react/24/outline';
import Breadcrumb from '../../components/Breadcrumb';
import Page from '../../components/Page';
import client from '../../lib/feathers';

// Regions are the curated browse areas behind the public site's Explore page.
// A region is a polygon; its wineries and appellations are derived from it at
// read time, never stored. See docs/PUBLIC_SITE_EXPLORE_PLAN.md.

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  draft: 'bg-gray-100 text-gray-800',
};

export default function Regions() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // $withCounts costs two spatial queries per region and there are a
      // handful of regions, so the list always asks for them: the counts are
      // the whole reason to look at this page.
      const res = await client.service('admin/region').find({
        query: { $withCounts: true, $sort: { name: 1 }, $limit: 200 },
      });
      setRegions(res.data || res);
    } catch (e) {
      setError(e.message || 'Could not load regions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Page title="Regions">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Regions' }]} />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/admin/regions/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-800 hover:bg-red-900"
          >
            New region
          </Link>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-500">
        A region is a drawn area. The wineries and appellations in it are worked
        out from the shape, so the counts below move on their own as listings
        are added or geocoded.
      </p>

      {loading && <p className="mt-6 text-sm text-gray-500">Loading...</p>}
      {error && <p className="mt-6 text-sm text-red-600">{error}</p>}

      {!loading && !error && regions.length === 0 && (
        <p className="mt-6 text-sm text-gray-500">
          No regions yet. Create one, then seed its shape from the appellations
          it covers.
        </p>
      )}

      {!loading && regions.length > 0 && (
        <div className="mt-6 flex flex-col">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shape</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Wineries</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Appellations</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {regions.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Link to={`/admin/regions/${r.id}`} className="text-red-800 hover:text-red-900">
                            {r.name}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{r.slug}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {/* The list never carries the polygon itself: returning
                              many at once took production down twice on
                              appellations. Only whether one exists. */}
                          {r.counts && (r.counts.wineries > 0 || r.counts.appellations > 0) ? (
                            <MapPinIcon className="h-5 w-5 text-green-500" title="Drawn" />
                          ) : (
                            <span className="text-gray-300" title="Not drawn yet">--</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {r.counts ? r.counts.wineries : '--'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {r.counts ? r.counts.appellations : '--'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_STYLES[r.status] || STATUS_STYLES.draft}`}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
